/**
 * How a voice call opens: a short chime, then Beacon speaks first. Kept free
 * of React and of the app's path aliases so it can be tested on its own.
 */

export interface Opening {
  /** Play the connected chime. */
  chime: boolean
  /** Have the model greet the person before they speak. */
  greet: boolean
}

/**
 * A fresh call chimes and greets. A reconnect does neither: the person is
 * mid-conversation, and a second "Ready when you are" would sound like a new
 * call. Sounds also respect a preference for silence when there is one; Helm
 * has none yet, so a fresh call always chimes.
 */
export function openingFor({
  reconnect,
  sounds = true,
}: {
  reconnect: boolean
  sounds?: boolean
}): Opening {
  return { chime: !reconnect && sounds, greet: !reconnect }
}

/** What the chime needs from Web Audio; a test passes a stand-in. */
export interface ChimeContext {
  currentTime: number
  destination: unknown
  state?: string
  resume?: () => Promise<void>
  close?: () => Promise<void>
  createOscillator(): {
    type: string
    frequency: { setValueAtTime(value: number, time: number): unknown }
    connect(node: unknown): unknown
    start(time: number): void
    stop(time: number): void
  }
  createGain(): {
    gain: {
      setValueAtTime(value: number, time: number): unknown
      linearRampToValueAtTime(value: number, time: number): unknown
      exponentialRampToValueAtTime(value: number, time: number): unknown
    }
    connect(node: unknown): unknown
  }
}

/** Two soft rising notes, E5 then A5: about a third of a second, well under speech level. */
export const CHIME_NOTES = [
  { frequency: 659.25, at: 0, length: 0.16 },
  { frequency: 880, at: 0.11, length: 0.22 },
] as const
const PEAK = 0.06

/** Plays the chime now on `context`; returns how long it lasts, in seconds. */
export function playChime(context: ChimeContext): number {
  const start = context.currentTime + 0.02
  for (const note of CHIME_NOTES) {
    const oscillator = context.createOscillator()
    const gain = context.createGain()
    oscillator.type = 'sine'
    oscillator.frequency.setValueAtTime(note.frequency, start + note.at)
    gain.gain.setValueAtTime(0.0001, start + note.at)
    gain.gain.linearRampToValueAtTime(PEAK, start + note.at + 0.015)
    gain.gain.exponentialRampToValueAtTime(0.0001, start + note.at + note.length)
    oscillator.connect(gain)
    gain.connect(context.destination)
    oscillator.start(start + note.at)
    oscillator.stop(start + note.at + note.length + 0.02)
  }
  const last = CHIME_NOTES[CHIME_NOTES.length - 1]!
  return last.at + last.length + 0.04
}

export interface PreparedChime {
  play: () => void
  /** Lets the audio go without playing, when the call never connected. */
  discard: () => void
}

/**
 * Gets the chime ready while the click that starts the call is still being
 * handled: Safari only lets audio start from a gesture, and the call connects
 * seconds later. Null where Web Audio is missing. Never throws: a chime is not
 * worth failing a call over.
 */
export function prepareChime(
  create: () => ChimeContext | null = () => {
    const Context =
      typeof window === 'undefined'
        ? undefined
        : (window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext)
    return Context ? (new Context() as unknown as ChimeContext) : null
  },
): PreparedChime | null {
  let context: ChimeContext | null
  try {
    context = create()
    if (context?.state === 'suspended') void context.resume?.().catch(() => undefined)
  } catch {
    return null
  }
  if (!context) return null
  const ready = context
  const discard = () => void ready.close?.().catch(() => undefined)
  return {
    play: () => {
      try {
        const seconds = playChime(ready)
        setTimeout(discard, (seconds + 0.2) * 1000)
      } catch {
        discard()
      }
    },
    discard,
  }
}
